// src/pages/MinistereInterieur.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
  Button,
} from '@mui/material';
import moment from 'moment';
import { getPublications } from '../services/publicationService';

function MinistereInterieur() {
  const [visibleCount, setVisibleCount] = useState(2);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [statistiquesData, setStatistiquesData] = useState([]);
  const [filteredStatistiques, setFilteredStatistiques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chargement des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'ami', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            annuaires: item.title,
            link: item.document_url,
            image: item.image_url,
            description: item.description,
            date: new Date(item.date),
          }));
          // Trier par date décroissante (plus récent en premier)
          const sorted = mapped.sort((a, b) => b.date - a.date);
          setStatistiquesData(sorted);
          setFilteredStatistiques(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    let filtered;
    if (year === 'Tous') {
      filtered = [...statistiquesData];
    } else {
      filtered = statistiquesData.filter((stat) => {
        const statYear = stat.date.getFullYear();
        return statYear === parseInt(year);
      });
    }
    // Trier par date décroissante
    filtered.sort((a, b) => b.date - a.date);
    setFilteredStatistiques(filtered);
    setVisibleCount(2); // réinitialiser l'affichage
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme({
    palette: {
      primary: { main: '#388E3C' },
      background: { default: '#f5f5f5' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      body1: {
        fontSize: 16,
        letterSpacing: 0.5,
        color: '#6B778C',
      },
      h4: { fontWeight: '600' },
    },
  });

  // Années disponibles à partir des données chargées
  const availableYears = useMemo(() => {
    const years = statistiquesData.map((stat) => stat.date.getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [statistiquesData]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
              Ministère de l'Intérieur, du Développement Communautaire et de la Sécurité Publique
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1">Chargement des données...</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
              Ministère de l'Intérieur, du Développement Communautaire et de la Sécurité Publique
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1" color="error">{error}</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
            Ministère de l'Intérieur, du Développement Communautaire et de la Sécurité Publique
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px' }}>
            Le Ministère de l’Intérieur, du Développement Communautaire et de la Sécurité Publique a pour mission de garantir la sécurité et le bien-être des citoyens. Il s’efforce d’améliorer la sécurité publique tout en soutenant le développement communautaire. Cette page fournit une vue d'ensemble des statistiques pertinentes concernant les efforts du ministère.
          </Typography>

          <FormControl fullWidth variant="outlined" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Filtrer par année de réalisation</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Filtrer par année de réalisation"
            >
              <MenuItem value="Tous">Tous les annuaires</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>

          <Grid container spacing={3} style={{ marginTop: '20px' }}>
            {filteredStatistiques.slice(0, visibleCount).map((statistique, index) => (
              <Grid item xs={12} sm={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex' }}>
                    <a
                      href={statistique.link}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <CardMedia
                        component="img"
                        sx={{ width: 71, height: 100, m: 2 }}
                        image={statistique.image}
                        alt={statistique.annuaires}
                      />
                    </a>
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: theme.palette.primary.main }}>
                        <a
                          href={statistique.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <DownloadIcon /> {statistique.annuaires}
                        </a>
                      </Typography>
                      <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                        {statistique.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Publié en {moment(statistique.date).format('MMMM YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredStatistiques.length && (
            <Button
              onClick={handleShowMore}
              style={{
                marginTop: '20px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              Plus de Statistiques
            </Button>
          )}
        </Container>
        <br />
        <br />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default MinistereInterieur;